// FileUpload — presentational upload primitives (paperclip button,
// progress row, drop overlay) the composer and timeline wire to
// `useFileUpload`.

import { Plus } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { AttachSheet } from "~/components/mobile/AttachSheet";
import type { UploadRecord } from "~/hooks/useFileUpload";
import { fileFamilyFor, type FileFamily } from "~/lib/file-kind";
import {
  mediaPickerErrorMessage,
  pickMobileMedia,
  type NativeMediaSource,
} from "~/lib/media-picker";
import { platform } from "~/lib/platform";
import { ICON_STROKE } from "~/lib/icon";
import { IconTooltip } from "~/components/IconTooltip";

/* ── paperclip button ─────────────────────────────────────────── */

export interface FileUploadButtonProps {
  onFiles: (files: readonly File[]) => void;
  accept?: string;
  disabled?: boolean;
  mobile?: boolean;
  onError?: (message: string) => void;
}

export function FileUploadButton({
  onFiles,
  accept,
  disabled,
  mobile = false,
  onError,
}: FileUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const nativeMobilePicker = mobile && platform === "tauri";

  const openFiles = useCallback(() => inputRef.current?.click(), []);
  const reportError = useCallback(
    (message: string) => {
      setAnnouncement(message);
      onError?.(message);
    },
    [onError],
  );
  const pickNative = useCallback(
    (source: NativeMediaSource) => {
      void pickMobileMedia(source)
        .then((file) => {
          if (file) onFiles([file]);
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          if (/cancelled|picker busy/i.test(message)) return;
          reportError(mediaPickerErrorMessage(source, err));
        });
    },
    [onFiles, reportError],
  );

  return (
    <>
      <IconTooltip label="Attach files">
        <button
          type="button"
          aria-label="Attach files"
          disabled={disabled}
          onClick={() => {
            if (nativeMobilePicker) setSheetOpen(true);
            else openFiles();
          }}
          className="chat-button"
          style={{
            // The native picker only exists on phones and tablets: give the
            // paperclip a 44px touch target there.
            width: nativeMobilePicker ? 44 : "var(--chat-composer-toolbar-icon-size)",
            height: nativeMobilePicker ? 44 : "var(--chat-composer-toolbar-icon-size)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: "var(--chat-radius-sm)",
            background: "transparent",
            color: nativeMobilePicker ? "var(--chat-fg-muted)" : "var(--chat-fg-secondary)",
            cursor: disabled ? "not-allowed" : "pointer",
            padding: 0,
            flexShrink: 0,
            transition:
              "background var(--chat-motion-fast) var(--chat-motion-ease), color var(--chat-motion-fast) var(--chat-motion-ease)",
          }}
        >
          {nativeMobilePicker ? (
            <Plus size={18} strokeWidth={ICON_STROKE} aria-hidden />
          ) : (
            <PaperclipGlyph />
          )}
        </button>
      </IconTooltip>
      <span className="chat-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(e) => {
          const list = e.target.files;
          if (!list || list.length === 0) return;
          const files: File[] = [];
          for (let i = 0; i < list.length; i += 1) {
            const f = list.item(i);
            if (f) files.push(f);
          }
          onFiles(files);
          // Reset so the same file can be picked twice in a row — the
          // `change` event only fires when the value actually changes.
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      {sheetOpen && nativeMobilePicker ? (
        <AttachSheet
          onCamera={() => pickNative("camera")}
          onPhotos={() => pickNative("photos")}
          onFiles={openFiles}
          onClose={() => setSheetOpen(false)}
        />
      ) : null}
    </>
  );
}

function PaperclipGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21.44 11.05 12.25 20.24a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

/* ── progress row ──────────────────────────────────────────────── */

export interface UploadProgressRowProps {
  record: UploadRecord;
  onCancel: () => void;
  onRetry: () => void;
}

export function UploadProgressRow({ record, onCancel, onRetry }: UploadProgressRowProps) {
  const { file, progress, status, reason } = record;
  const isFailed = status === "failed";
  const isDone = status === "done";
  const showRetry = isFailed && reason !== "too_large";
  return (
    <div
      className="chat-upload-row"
      data-status={status}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-upload-row-height)",
        paddingInline: "var(--chat-space-2)",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "var(--chat-upload-icon-size)",
          height: "var(--chat-upload-icon-size)",
          color: "var(--chat-fg-secondary)",
          flexShrink: 0,
        }}
      >
        <UploadFamilyGlyph family={fileFamilyFor(file.name, file.type)} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file.name}
      </span>
      {isFailed && (
        <span
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            whiteSpace: "nowrap",
          }}
        >
          {failReasonLabel(reason)}
        </span>
      )}
      <ProgressBar
        progress={progress}
        state={isFailed ? "failed" : isDone ? "done" : "active"}
        label={`Uploading ${file.name}`}
      />
      <span className="chat-visually-hidden" role="status" aria-live="polite">
        {isFailed ? `${file.name} · ${failReasonLabel(reason)}` : ""}
      </span>
      {isFailed ? (
        <>
          {showRetry && (
            <button
              type="button"
              onClick={onRetry}
              aria-label={`Retry uploading ${file.name}`}
              className="chat-button"
              style={iconButtonStyle}
            >
              <RetryGlyph />
            </button>
          )}
          {/* Dismiss — drops the row from the progress tray so a
              user who doesn't want to retry can clear the error. */}
          <button
            type="button"
            onClick={onCancel}
            aria-label={`Dismiss ${file.name}`}
            className="chat-button"
            style={iconButtonStyle}
          >
            <CancelGlyph />
          </button>
        </>
      ) : status === "uploading" ? (
        <button
          type="button"
          onClick={onCancel}
          aria-label={`Cancel upload ${file.name}`}
          className="chat-button"
          style={iconButtonStyle}
        >
          <CancelGlyph />
        </button>
      ) : (
        <span aria-hidden style={reservedSlotStyle} />
      )}
    </div>
  );
}

const iconButtonStyle: CSSProperties = {
  width: "var(--chat-upload-cancel-size)",
  height: "var(--chat-upload-cancel-size)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  padding: 0,
  cursor: "pointer",
  flexShrink: 0,
};

const reservedSlotStyle: CSSProperties = {
  width: "var(--chat-upload-cancel-size)",
  height: "var(--chat-upload-cancel-size)",
  flexShrink: 0,
};

function failReasonLabel(reason: UploadRecord["reason"]): string {
  return reason === "too_large" ? "Too large to send" : "Couldn't send";
}

function ProgressBar({
  progress,
  state,
  label,
}: {
  progress: number;
  state: "active" | "failed" | "done";
  label: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(progress)));
  const fill = state === "failed" ? "var(--chat-state-danger)" : "var(--chat-fg-secondary)";
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        position: "relative",
        display: "inline-block",
        width: "var(--chat-upload-progress-width)",
        height: "var(--chat-upload-progress-height)",
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-raised)",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: `${clamped}%`,
          background: fill,
          transition: "width var(--chat-motion-fast) var(--chat-motion-ease)",
          borderRadius: "var(--chat-radius-full)",
        }}
      />
    </span>
  );
}

function CancelGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function RetryGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <polyline points="21 3 21 8 16 8" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <polyline points="3 21 3 16 8 16" />
    </svg>
  );
}

function UploadFamilyGlyph({ family }: { family: FileFamily }) {
  const base = {
    width: "100%",
    height: "100%",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: ICON_STROKE,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (family) {
    case "image":
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      );
    case "video":
      return (
        <svg {...base}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <polygon points="10 9 15 12 10 15 10 9" />
        </svg>
      );
    case "spreadsheet":
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
        </svg>
      );
    case "archive":
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M12 7v2M12 11v2M12 15v2" />
        </svg>
      );
    case "code":
      return (
        <svg {...base}>
          <path d="M14 3v4a2 2 0 0 0 2 2h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
          <path d="m10 13-2 2 2 2M14 13l2 2-2 2" />
        </svg>
      );
    case "document":
    case "generic":
    default:
      return (
        <svg {...base}>
          <path d="M14 3v4a2 2 0 0 0 2 2h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
        </svg>
      );
  }
}

/* ── drop zone ─────────────────────────────────────────────────── */

export interface DropZoneProps {
  /** Fires for each non-empty drop. The drop zone already filters
   *  out drags that don't carry files. */
  onDrop: (files: readonly File[]) => void;
  children: ReactNode;
  /** Force the overlay independent of the internal HTML5 drag state —
   *  used by Storybook and by the desktop (Tauri) native drag-drop
   *  path, where drags never reach the DOM handlers (#7755). */
  overlayForceVisible?: boolean;
  /** Accessible label for the overlay. Screen readers announce this
   *  on overlay appearance via the embedded live region. */
  label?: string;
}

export function DropZone({
  onDrop,
  children,
  overlayForceVisible = false,
  label = "Drop files to upload",
}: DropZoneProps) {
  const [active, setActive] = useState(false);
  // We keep a counter so nested `dragleave` events fired from child
  // nodes don't prematurely hide the overlay. Only when the counter
  // returns to zero (= leaving the outer container) do we dismiss.
  const dragDepthRef = useRef(0);

  const hasFiles = useCallback((e: React.DragEvent) => {
    // `types` is the reliable source — older iOS Safari won't populate
    // `files` during dragover, but `types` includes `"Files"`.
    return Array.from(e.dataTransfer.types).includes("Files");
  }, []);

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setActive(true);
    },
    [hasFiles],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      // Signal a copy operation so the drag cursor shows a + glyph.
      e.dataTransfer.dropEffect = "copy";
    },
    [hasFiles],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    // Only drags that carry files set our depth counter on enter —
    // mirror that here so we don't decrement into negative for
    // text/plain drags.
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setActive(false);
  }, []);

  const onDropEvt = useCallback(
    (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setActive(false);
      const list = e.dataTransfer.files;
      if (!list || list.length === 0) return;
      const files: File[] = [];
      for (let i = 0; i < list.length; i += 1) {
        const f = list.item(i);
        if (f) files.push(f);
      }
      if (files.length > 0) onDrop(files);
    },
    [hasFiles, onDrop],
  );

  useEffect(() => {
    // Belt-and-braces reset for drag sequences that end outside our
    // container (user drags back to their desktop, or drops on
    // something else in the page). The browser fires `dragend` on
    // the source element; we listen on `window` so we catch it
    // regardless of where the cursor is. The `drop` handler only
    // resets our local state — it does NOT cancel the drop for the
    // receiver, because we don't `preventDefault` here.
    const reset = () => {
      dragDepthRef.current = 0;
      setActive(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, []);

  const overlayVisible = active || overlayForceVisible;

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropEvt}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {children}
      {overlayVisible && <DropOverlay label={label} />}
    </div>
  );
}

function DropOverlay({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "var(--chat-drop-overlay-gap)",
        background: "var(--chat-surface-overlay)",
        opacity: "var(--chat-drop-overlay-opacity)",
        pointerEvents: "none",
        transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
        zIndex: 5,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: "var(--chat-drop-overlay-inset)",
          border: "1px solid var(--chat-fg-muted)",
          borderRadius: "var(--chat-radius-lg)",
        }}
      />
      <span
        aria-hidden
        style={{
          width: "var(--chat-drop-overlay-glyph-size)",
          height: "var(--chat-drop-overlay-glyph-size)",
          color: "var(--chat-fg-secondary)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <UploadCloudGlyph />
      </span>
      <span
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 300,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function UploadCloudGlyph() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      <path d="M16 16l-4-4-4 4" />
    </svg>
  );
}
